import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  Alert,
  ScrollView,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { notificationsApi, changeRequestsApi } from '../../services/apiService';
import { NotificationList } from '../../types';
import { COLORS, FONTS } from '../../constants/config';
import { NotificationsSkeleton, EmptyState, PageContainer, Navbar, Breadcrumb, SKELETON_DIMENSIONS } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useResponsive } from '../../hooks';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useNotificationsQuery, useMarkNotificationReadMutation, useMarkAllNotificationsReadMutation } from '../../hooks/queries';
import { SUPPORT_CATEGORIES } from '../support/supportCategories';

import { tokens } from '@design';
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// Category definitions for filter tabs
type NotificationCategory = 'all' | 'goals' | 'initiatives' | 'kpis' | 'approvals' | 'comments' | 'evaluation' | 'system' | 'support';

interface CategoryConfig {
  id: NotificationCategory;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
  types: string[]; // notification_type values that belong to this category
}

const NOTIFICATION_CATEGORIES_CONFIG: Omit<CategoryConfig, 'label'>[] = [
  { id: 'support', icon: 'help-buoy-outline', types: ['ticket_update'] },
  { id: 'system', icon: 'settings-outline', types: ['system'] },
  { id: 'comments', icon: 'chatbubble-outline', types: ['comment', 'comment_reply', 'comment_mention'] },
  { id: 'approvals', icon: 'checkmark-done-outline', types: ['change_request', 'change_approved', 'change_rejected'] },
  { id: 'evaluation', icon: 'clipboard-outline', types: ['evaluation_assigned', 'evaluation_submitted', 'evaluation_approved', 'evaluation_returned'] },
  { id: 'kpis', icon: 'analytics-outline', types: ['kpi_due', 'kpi_overdue'] },
  { id: 'initiatives', icon: 'rocket-outline', types: ['initiative_assigned', 'initiative_submitted', 'initiative_approved', 'initiative_rejected', 'initiative_update'] },
  { id: 'goals', icon: 'flag-outline', types: ['goal_assigned', 'goal_submitted', 'goal_approved', 'goal_rejected', 'goal_deadline', 'goal_deleted', 'goal_edited'] },
  { id: 'all', icon: 'notifications-outline', types: [] },
];

const NOTIFICATION_ICONS: Record<string, keyof typeof Ionicons.glyphMap> = {
  // KPI notifications
  kpi_due: 'time-outline',
  kpi_overdue: 'warning-outline',
  // Goal notifications
  goal_assigned: 'flag-outline',
  goal_submitted: 'paper-plane-outline',
  goal_approved: 'checkmark-circle-outline',
  goal_rejected: 'close-circle-outline',
  goal_deadline: 'alarm-outline',
  goal_deleted: 'trash-outline',
  goal_edited: 'create-outline',
  // Initiative notifications
  initiative_assigned: 'rocket-outline',
  initiative_submitted: 'paper-plane-outline',
  initiative_approved: 'checkmark-circle-outline',
  initiative_rejected: 'close-circle-outline',
  initiative_update: 'sync-outline',
  // Change request notifications
  change_request: 'document-text-outline',
  change_approved: 'checkmark-done-outline',
  change_rejected: 'ban-outline',
  // Evaluation notifications
  evaluation_assigned: 'clipboard-outline',
  evaluation_submitted: 'paper-plane-outline',
  evaluation_approved: 'checkmark-circle-outline',
  evaluation_returned: 'return-up-back-outline',
  // System notifications
  system: 'notifications-outline',
  // Support notifications
  ticket_update: 'help-buoy-outline',
};

const NOTIFICATION_COLORS: Record<string, string> = {
  // KPI notifications
  kpi_due: tokens.colors.raw.yellow["500"],  // Yellow
  kpi_overdue: tokens.colors.raw.red["500"],  // Red
  // Goal notifications - pending (yellow)
  goal_assigned: tokens.colors.raw.blue["500"],  // Blue
  goal_submitted: tokens.colors.raw.yellow["500"],  // Yellow
  goal_approved: tokens.colors.raw.green["500"],  // Green
  goal_rejected: tokens.colors.raw.red["500"],  // Red
  goal_deadline: tokens.colors.raw.yellow["500"],  // Yellow
  goal_deleted: tokens.colors.raw.red["500"],  // Red
  goal_edited: tokens.colors.raw.blue["500"],  // Blue
  // Initiative notifications
  initiative_assigned: tokens.colors.raw.blue["500"],  // Blue
  initiative_submitted: tokens.colors.raw.yellow["500"],  // Yellow
  initiative_approved: tokens.colors.raw.green["500"],  // Green
  initiative_rejected: tokens.colors.raw.red["500"],  // Red
  initiative_update: tokens.colors.raw.slate["500"],  // Gray
  // Change request notifications
  change_request: tokens.colors.raw.yellow["500"],  // Yellow
  change_approved: tokens.colors.raw.green["500"],  // Green
  change_rejected: tokens.colors.raw.red["500"],  // Red
  // Evaluation notifications
  evaluation_assigned: tokens.colors.raw.blue["500"],  // Blue
  evaluation_submitted: tokens.colors.raw.yellow["500"],  // Yellow
  evaluation_approved: tokens.colors.raw.green["500"],  // Green
  evaluation_returned: tokens.colors.raw.red["500"],  // Red
  // System notifications
  system: tokens.colors.semantic.general["Main Color"],  // Brand primary
  // Support notifications
  ticket_update: tokens.colors.raw.blue["500"],  // Blue
};

export default function NotificationsScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { isMobile, isSmallMobile, containerPadding } = useResponsive();
  const { user } = useAuth();
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<NotificationCategory>('all');

  // Build categories with translated labels.
  // Evaluation tab is only relevant to evaluator/approver/superuser — hide it
  // for other roles so they don't see an always-empty tab.
  const userType = user?.profile?.user_type;
  const canSeeEvaluationTab =
    userType === 'performance_evaluator' ||
    userType === 'evaluation_approver' ||
    userType === 'superuser';

  const NOTIFICATION_CATEGORIES: CategoryConfig[] = useMemo(() =>
    NOTIFICATION_CATEGORIES_CONFIG
      .filter(cat => cat.id !== 'evaluation' || canSeeEvaluationTab)
      .map(cat => ({
        ...cat,
        label: cat.id === 'all' ? t('common.all') : t(`notifications.categories.${cat.id}`),
      })),
    [t, canSeeEvaluationTab]
  );

  // Theme colors for dark mode
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: colors.backgroundAlt,
    sectionBg: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
  };

  // React Query data fetching
  const {
    data: notificationsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useNotificationsQuery(user?.id);

  const notifications = notificationsData?.results || [];

  // Filter notifications by selected category
  const filteredNotifications = useMemo(() => {
    if (selectedCategory === 'all') {
      return notifications;
    }
    const category = NOTIFICATION_CATEGORIES.find(c => c.id === selectedCategory);
    if (!category) return notifications;
    return notifications.filter((n: NotificationList) => category.types.includes(n.notification_type));
  }, [notifications, selectedCategory, NOTIFICATION_CATEGORIES]);

  // Calculate unread count per category
  const categoryUnreadCounts = useMemo(() => {
    const counts: Record<NotificationCategory, number> = {
      all: 0,
      goals: 0,
      initiatives: 0,
      kpis: 0,
      approvals: 0,
      comments: 0,
      evaluation: 0,
      system: 0,
      support: 0,
    };

    notifications.forEach((n: NotificationList) => {
      if (!n.is_read) {
        counts.all++;
        // Find which category this notification belongs to
        for (const category of NOTIFICATION_CATEGORIES) {
          if (category.id !== 'all' && category.types.includes(n.notification_type)) {
            counts[category.id]++;
            break;
          }
        }
      }
    });

    return counts;
  }, [notifications, NOTIFICATION_CATEGORIES]);

  const markReadMutation = useMarkNotificationReadMutation();
  const markAllReadMutation = useMarkAllNotificationsReadMutation();


  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  const handleNotificationPress = async (notification: NotificationList) => {
    // Mark as read first
    if (!notification.is_read) {
      try {
        await notificationsApi.markRead(notification.id);
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    // Navigate to related item
    navigateToRelatedItem(notification);
  };

  const navigateToRelatedItem = async (notification: NotificationList) => {
    const { related_object_type, related_object_id } = notification;

    // Debug: Log notification details
    console.log('=== Navigation Debug ===');
    console.log('Notification ID:', notification.id);
    console.log('related_object_type:', related_object_type);
    console.log('related_object_id:', related_object_id);

    // Helper function to navigate based on user role
    const navigateToGoal = (goalId: number) => {
      if (user?.profile?.user_type === 'org_employee') {
        navigation.navigate('GoalsStack' as any, {
          screen: 'GoalDetail',
          params: { id: goalId },
        });
      } else {
        navigation.navigate('StrategyStack' as any, {
          screen: 'GoalDetail',
          params: { id: goalId },
        });
      }
    };

    const navigateToInitiative = (initiativeId: number) => {
      navigation.navigate('InitiativesStack' as any, {
        screen: 'InitiativeDetail',
        params: { id: initiativeId },
      });
    };

    const navigateToKPI = (kpiId: number) => {
      navigation.navigate('GoalsStack' as any, {
        screen: 'KPIDetail',
        params: { id: kpiId },
      });
    };

    const navigateToSupportTicket = (requestType: string, ticketId: number) => {
      const cat = SUPPORT_CATEGORIES.find(c => c.requestType === requestType);
      if (!cat) return;
      navigation.navigate('SupportStack' as any, {
        screen: cat.route,
        params: { requestType, openTicketId: ticketId },
      });
    };

    // Evaluation routing — role-aware: approver lands on the approval review
    // surface, evaluator on the scoring screen, everyone else on the read-only
    // result view.
    const isApprover = userType === 'evaluation_approver' || userType === 'superuser';

    const navigateToEvaluationFramework = (frameworkId: number) => {
      // Approvers review framework details inside FrameworkBuilder (read-only
      // mode is handled inside the screen). Evaluators reopen their framework
      // there too — same screen, different state.
      if (isApprover || userType === 'performance_evaluator') {
        navigation.navigate('EvaluationStack' as any, {
          screen: 'FrameworkBuilder',
          params: { frameworkId },
        });
      } else {
        navigation.navigate('EvaluationStack' as any, { screen: 'EvaluationDashboard' });
      }
    };

    const navigateToAssignment = (assignmentId: number) => {
      navigation.navigate('EvaluationStack' as any, {
        screen: 'EvaluationDetail',
        params: { assignmentId },
      });
    };

    const navigateToEntityEvaluation = (evaluationId: number) => {
      // Approver reviewing a submitted evaluation goes to the management list
      // where they can approve/return; everyone else opens the read-only view.
      if (isApprover && notification.notification_type === 'evaluation_submitted') {
        navigation.navigate('EvaluationStack' as any, { screen: 'EvaluationManagement' });
      } else {
        navigation.navigate('EvaluationStack' as any, {
          screen: 'EvaluationReadOnly',
          params: { evaluationId },
        });
      }
    };

    // First try using related_object_type and related_object_id
    if (related_object_type && related_object_id) {
      switch (related_object_type) {
        case 'goal':
          navigateToGoal(related_object_id);
          return;

        case 'initiative':
          navigateToInitiative(related_object_id);
          return;

        case 'kpi':
          navigateToKPI(related_object_id);
          return;

        case 'evaluation_framework':
          navigateToEvaluationFramework(related_object_id);
          return;

        case 'evaluation_assignment':
          navigateToAssignment(related_object_id);
          return;

        case 'entity_evaluation':
          navigateToEntityEvaluation(related_object_id);
          return;

        case 'change_request':
          // Open approval modal for change requests
          try {
            setLoadingChangeRequest(true);
            const changeRequest = await changeRequestsApi.get(related_object_id);
            setSelectedChangeRequest(changeRequest);
            setShowApprovalModal(true);
          } catch (err: any) {
            console.error('Error fetching change request:', err);
            Alert.alert(t('common.error'), t('notifications.failedToLoadRequest'));
          } finally {
            setLoadingChangeRequest(false);
          }
          return;

        default:
          console.log('Unknown related_object_type:', related_object_type);
      }
    }

    // Fallback: Parse the link field for older notifications
    const link = notification.link;
    if (link) {
      // Parse link like /goals/5 or /initiatives/10
      const goalMatch = link.match(/\/goals\/(\d+)/);
      if (goalMatch) {
        navigateToGoal(parseInt(goalMatch[1], 10));
        return;
      }

      const initiativeMatch = link.match(/\/initiatives\/(\d+)/);
      if (initiativeMatch) {
        navigateToInitiative(parseInt(initiativeMatch[1], 10));
        return;
      }

      const kpiMatch = link.match(/\/kpis\/(\d+)/);
      if (kpiMatch) {
        navigateToKPI(parseInt(kpiMatch[1], 10));
        return;
      }

      const frameworkMatch = link.match(/\/evaluation\/frameworks\/(\d+)/);
      if (frameworkMatch) {
        navigateToEvaluationFramework(parseInt(frameworkMatch[1], 10));
        return;
      }

      const assignmentMatch = link.match(/\/evaluation\/assignments\/(\d+)/);
      if (assignmentMatch) {
        navigateToAssignment(parseInt(assignmentMatch[1], 10));
        return;
      }

      const evaluationMatch = link.match(/\/evaluation\/evaluations\/(\d+)/);
      if (evaluationMatch) {
        navigateToEntityEvaluation(parseInt(evaluationMatch[1], 10));
        return;
      }

      const supportMatch = link.match(/\/support\/([a-z_]+)\/(\d+)/);
      if (supportMatch) {
        navigateToSupportTicket(supportMatch[1], parseInt(supportMatch[2], 10));
        return;
      }
    }

    console.log('No navigation target found for this notification');
  };


  const handleMarkAllRead = useCallback(async () => {
    try {
      await notificationsApi.markAllRead();
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, [queryClient]);

  const renderItem = ({ item }: { item: NotificationList }) => {
    const iconColor = NOTIFICATION_COLORS[item.notification_type] || COLORS.primary;
    const iconBgColor = iconColor + '15';

    return (
      <TouchableOpacity
        style={[
          styles.card,
          { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder },
          !item.is_read && [styles.cardUnread, { backgroundColor: isDark ? '#422006' : '#FFFBEB', borderColor: isDark ? '#78350F' : '#FDE68A' }],
          { minHeight: SKELETON_DIMENSIONS.NOTIFICATION_ITEM_HEIGHT }
        ]}
        onPress={() => handleNotificationPress(item)}
        activeOpacity={0.7}
      >
        {/* Icon */}
        <View style={[styles.iconContainer, { backgroundColor: iconBgColor }]}>
          <Ionicons
            name={NOTIFICATION_ICONS[item.notification_type] || 'notifications-outline'}
            size={22}
            color={iconColor}
          />
        </View>

        {/* Content */}
        <View style={styles.content}>
          {/* Title */}
          <Text style={[styles.title, { color: themeColors.textSecondary }, !item.is_read && [styles.titleUnread, { color: themeColors.textPrimary }]]}>
            {item.title}
          </Text>

          {/* Message */}
          {item.message && (
            <Text style={[styles.message, { color: themeColors.textMuted }]} numberOfLines={2}>
              {item.message}
            </Text>
          )}

          {/* Meta row: Organization & Time */}
          <View style={styles.metaRow}>
            {item.department_name && (
              <View style={[styles.orgBadge, { backgroundColor: isDark ? '#1C2150' : tokens.colors.raw.indigo["50"] }]}>
                <Text style={styles.orgText}>{item.department_name}</Text>
              </View>
            )}
            <Text style={[styles.time, { color: themeColors.textMuted }]}>
              {item.time_ago || item.notification_type_display}
            </Text>
          </View>
        </View>

        {/* Unread indicator */}
        {!item.is_read && <View style={styles.unreadDot} />}

        {/* Navigation arrow */}
        {item.related_object_type && item.related_object_id && (
          <Ionicons
            name="chevron-back-outline"
            size={18}
            color={themeColors.textMuted}
            style={styles.arrow}
          />
        )}
      </TouchableOpacity>
    );
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Navbar title={t('notifications.title')} />
        <NotificationsSkeleton />
      </PageContainer>
    );
  }

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  // Get current category config for empty state
  const currentCategoryConfig = NOTIFICATION_CATEGORIES.find(c => c.id === selectedCategory);

  // Get category-specific empty state message
  const getEmptyStateMessage = () => {
    switch (selectedCategory) {
      case 'goals':
        return t('notifications.noGoalsNotifications');
      case 'initiatives':
        return t('notifications.noInitiativesNotifications');
      case 'kpis':
        return t('notifications.noKpisNotifications');
      case 'approvals':
        return t('notifications.noApprovalsNotifications');
      case 'comments':
        return t('notifications.noCommentsNotifications');
      case 'evaluation':
        return t('notifications.noEvaluationNotifications');
      case 'system':
        return t('notifications.noSystemNotifications');
      default:
        return t('notifications.newNotificationsWillAppear');
    }
  };

  return (
    <>
      <PageContainer>
        <Navbar title={t('notifications.title')} />
        {/* Breadcrumb with sidebar toggle */}
        <Breadcrumb
          items={[
            { label: t('breadcrumb.home') },
            { label: t('notifications.title') },
          ]}
        />

        {/* Header with mark all read */}
        {unreadCount > 0 && (
          <View style={[styles.header, { backgroundColor: themeColors.card, borderBottomColor: themeColors.border }]}>
            <TouchableOpacity style={[styles.markAllButton, { backgroundColor: isDark ? '#1C2150' : tokens.colors.raw.indigo["50"] }]} onPress={handleMarkAllRead}>
              <Ionicons name="checkmark-done-outline" size={18} color={tokens.colors.semantic.general["Main Color"]} />
              <Text style={styles.markAllText}>{t('notifications.markAllRead')} ({unreadCount})</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Category Filter Tabs */}
        <View style={[styles.categoryTabsContainer, { backgroundColor: themeColors.card, borderBottomColor: themeColors.border }]}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.categoryTabsScroll}
            // RTL: Start from right side
            style={{ direction: 'rtl' }}
          >
            {NOTIFICATION_CATEGORIES.map((category) => {
              const isActive = selectedCategory === category.id;
              const unreadInCategory = categoryUnreadCounts[category.id];

              return (
                <TouchableOpacity
                  key={category.id}
                  style={[
                    styles.categoryTab,
                    { backgroundColor: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["100"] },
                    isActive && styles.categoryTabActive,
                  ]}
                  onPress={() => setSelectedCategory(category.id)}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name={category.icon}
                    size={18}
                    color={isActive ? tokens.colors.raw.white : themeColors.textMuted}
                    style={styles.categoryTabIcon}
                  />
                  <Text
                    style={[
                      styles.categoryTabText,
                      { color: themeColors.textMuted },
                      isActive && styles.categoryTabTextActive,
                    ]}
                  >
                    {category.label}
                  </Text>
                  {unreadInCategory > 0 && (
                    <View
                      style={[
                        styles.categoryBadge,
                        isActive && styles.categoryBadgeActive,
                      ]}
                    >
                      <Text
                        style={[
                          styles.categoryBadgeText,
                          isActive && styles.categoryBadgeTextActive,
                        ]}
                      >
                        {unreadInCategory > 99 ? '99+' : unreadInCategory}
                      </Text>
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>

        {error ? (
          <EmptyState
            icon="alert-circle-outline"
            title={t('common.error')}
            message={error?.message || t('notifications.failedToLoad')}
            actionLabel={t('common.retry')}
            onAction={refetch}
          />
        ) : filteredNotifications.length === 0 ? (
          <EmptyState
            icon={currentCategoryConfig?.icon || 'notifications-outline'}
            title={selectedCategory === 'all' ? t('notifications.noNotifications') : t('notifications.noNotificationsInCategory', { category: currentCategoryConfig?.label || '' })}
            message={getEmptyStateMessage()}
          />
        ) : (
          <FlatList
            data={filteredNotifications}
            renderItem={renderItem}
            keyExtractor={(item) => item.id.toString()}
            contentContainerStyle={[styles.list, { padding: containerPadding, minHeight: 500 }]}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                colors={[tokens.colors.semantic.general["Main Color"]]}
                tintColor={tokens.colors.semantic.general["Main Color"]}
              />
            }
            showsVerticalScrollIndicator={false}
          />
        )}
      </PageContainer>

    </>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  header: {
    padding: tokens.spacing.absolute["4"],
    backgroundColor: tokens.colors.raw.white,
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["200"],
    alignItems: 'flex-end',
  },
  markAllButton: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
    paddingVertical: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    backgroundColor: tokens.colors.raw.indigo["50"],
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  markAllText: {
    color: tokens.colors.semantic.general["Main Color"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
  },
  // Category Filter Tabs styles
  categoryTabsContainer: {
    backgroundColor: tokens.colors.raw.white,
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["200"],
    paddingVertical: tokens.spacing.absolute["3"],
  },
  categoryTabsScroll: {
    paddingHorizontal: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
    flexDirection: 'row-reverse',
  },
  categoryTab: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    paddingVertical: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["3_5"],
    borderRadius: tokens.radii.absolute["radius-20"],
    backgroundColor: tokens.colors.raw.gray["100"],
    gap: tokens.spacing.absolute["1_5"],
  },
  categoryTabActive: {
    backgroundColor: tokens.colors.semantic.general["Main Color"],
  },
  categoryTabIcon: {
    marginStart: tokens.spacing.absolute["0_5"],
  },
  categoryTabText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["500"],
  },
  categoryTabTextActive: {
    color: tokens.colors.raw.white,
  },
  categoryBadge: {
    backgroundColor: tokens.colors.raw.red["500"],
    borderRadius: tokens.radii.absolute["radius-10"],
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.absolute["1_5"],
    marginEnd: tokens.spacing.absolute["0_5"],
  },
  categoryBadgeActive: {
    backgroundColor: tokens.colors.raw.white,
  },
  categoryBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
    fontWeight: '700',
  },
  categoryBadgeTextActive: {
    color: tokens.colors.semantic.general["Main Color"],
  },
  list: {
    padding: tokens.spacing.absolute["4"],
    paddingBottom: tokens.spacing.absolute["8"],
  },
  card: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    marginBottom: tokens.spacing.absolute["3"],
    padding: tokens.spacing.absolute["4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    overflow: 'hidden',
  },
  cardUnread: {
    backgroundColor: '#FFFBEB',
    borderColor: '#FDE68A',
    borderEndWidth: 4,
    borderEndColor: tokens.colors.semantic.general["Main Color"],
  },
  iconContainer: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    marginStart: tokens.spacing.absolute["3"],
  },
  content: {
    flex: 1,
    overflow: 'hidden',
  },
  title: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["700"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["1"],
  },
  titleUnread: {
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["900"],
  },
  message: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
    marginBottom: tokens.spacing.absolute["2"],
  },
  metaRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: tokens.spacing.absolute["1"],
    overflow: 'hidden',
  },
  orgBadge: {
    backgroundColor: tokens.colors.raw.indigo["50"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: 3,
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  orgText: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general["Main Color"],
  },
  time: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["400"],
  },
  unreadDot: {
    width: 10,
    height: 10,
    borderRadius: tokens.radii.absolute["radius-5"],
    backgroundColor: tokens.colors.semantic.general["Main Color"],
    marginEnd: tokens.spacing.absolute["2"],
    marginTop: tokens.spacing.absolute["1"],
  },
  arrow: {
    marginStart: tokens.spacing.absolute["2"],
    marginTop: tokens.spacing.absolute["0_5"],
  },
});

const styles = getStyles(true);
